import * as React from 'react';

import { cn } from '../lib/cn';

interface ButtonGroupContextValue {
  value?: string;
  onChange?: (value: string) => void;
  size: 'sm' | 'md' | 'lg';
}

const ButtonGroupContext = React.createContext<ButtonGroupContextValue | null>(null);

export interface ButtonGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  size?: 'sm' | 'md' | 'lg';
}

export const ButtonGroup = React.forwardRef<HTMLDivElement, ButtonGroupProps>(
  ({ className, value, defaultValue, onValueChange, size = 'md', children, ...props }, ref) => {
    const [internal, setInternal] = React.useState<string | undefined>(defaultValue);
    const isControlled = value !== undefined;
    const current = isControlled ? value : internal;

    const handleChange = React.useCallback(
      (next: string) => {
        if (!isControlled) setInternal(next);
        onValueChange?.(next);
      },
      [isControlled, onValueChange],
    );

    return (
      <ButtonGroupContext.Provider value={{ value: current, onChange: handleChange, size }}>
        <div
          ref={ref}
          role="group"
          className={cn(
            'inline-flex rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-2)] p-0.5',
            className,
          )}
          {...props}
        >
          {children}
        </div>
      </ButtonGroupContext.Provider>
    );
  },
);
ButtonGroup.displayName = 'ButtonGroup';

const sizeMap = {
  sm: 'h-7 px-2 text-xs',
  md: 'h-8 px-3 text-sm',
  lg: 'h-10 px-4 text-base',
};

export interface ButtonGroupItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
}

export const ButtonGroupItem = React.forwardRef<HTMLButtonElement, ButtonGroupItemProps>(
  ({ className, value, children, ...props }, ref) => {
    const ctx = React.useContext(ButtonGroupContext);
    if (!ctx) throw new Error('ButtonGroupItem must be used within ButtonGroup');
    const active = ctx.value === value;
    return (
      <button
        ref={ref}
        type="button"
        aria-pressed={active}
        onClick={() => ctx.onChange?.(value)}
        className={cn(
          'inline-flex items-center justify-center rounded-[var(--radius-sm)] font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]',
          sizeMap[ctx.size],
          active
            ? 'bg-[var(--color-accent)] text-white'
            : 'text-[var(--color-fg-secondary)] hover:bg-[var(--color-subtle)] hover:text-[var(--color-fg)]',
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  },
);
ButtonGroupItem.displayName = 'ButtonGroupItem';
